import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import { JourneyService } from "@services/journey/journeyService";
import { JourneyStepDimension } from "@services/journey/commands/cmdJourneyStepDimensions";

const service = new JourneyService();

// A step's dimensions are the cells pricing and conversions address — "Register for Classes /
// Fall 2026". Every contract can price each one differently, which is why they are billing keys
// rather than labels, and why the API refuses to delete one that is priced or converted against.
//
// Deliberately not offering the tracked-data dimension catalog's values here. A conversion rule
// is the only bridge between a data value and a billing cell, and the two are kept independent on
// purpose (journey/tests/test_golden_loop_period_cells) — offering catalog values as suggestions
// would imply a link that does not exist.

interface Props {
  journeyId: number;
  stepId: number;
  stepName: string;
  onClose: () => void;
}

interface Draft {
  code: string;
  name: string;
  sequence: number;
}

const EMPTY: Draft = { code: "", name: "", sequence: 0 };

export default function StepDimensionsModal({ journeyId, stepId, stepName, onClose }: Props) {
  const [dimensions, setDimensions] = useState<JourneyStepDimension[]>([]);
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    service
      .listStepDimensions(journeyId, stepId)
      .then(setDimensions)
      .catch(() => setError("Could not load dimensions"));

  useEffect(() => {
    load();
  }, [journeyId, stepId]);

  const startNew = () => {
    setError(null);
    setDraft({ ...EMPTY, sequence: dimensions.length });
    setEditing("new");
  };

  const startEdit = (dimension: JourneyStepDimension) => {
    setError(null);
    setDraft({ code: dimension.code, name: dimension.name, sequence: dimension.sequence });
    setEditing(dimension.id);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = { ...draft, code: draft.code.trim(), name: draft.name.trim() };
    try {
      if (editing === "new") await service.createStepDimension(journeyId, stepId, payload);
      else if (editing !== null) await service.updateStepDimension(journeyId, stepId, editing, payload);
      setEditing(null);
      await load();
    } catch (e) {
      // The API rejects a code already used on this step, and says so. Show that verbatim.
      setError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (dimension: JourneyStepDimension) => {
    setError(null);
    try {
      await service.deleteStepDimension(journeyId, stepId, dimension.id);
      await load();
    } catch (e) {
      // Refused while a pricesheet item or a recorded conversion still points here, with the
      // count in the message. That count is the whole explanation — don't replace it.
      setError(e instanceof Error ? e.message : "Could not delete");
    }
  };

  const valid = draft.code.trim() !== "" && draft.name.trim() !== "";

  return (
    <Modal show onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="mb-0">
          Dimensions
          <span className="text-secondary fw-normal"> for {stepName}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-secondary small">
          The parts of this step that price and convert separately, usually one per term. A
          contract can put a different price on each.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Table size="sm" className="align-middle mb-3">
          <thead>
            <tr className="small text-uppercase text-secondary">
              <th style={{ width: "30%" }}>Code</th>
              <th>Name</th>
              <th style={{ width: "6rem" }}>Order</th>
              <th style={{ width: "9rem" }} />
            </tr>
          </thead>
          <tbody>
            {dimensions.map((dimension) =>
              editing === dimension.id ? (
                <DraftRow
                  key={dimension.id}
                  draft={draft}
                  setDraft={setDraft}
                  valid={valid}
                  saving={saving}
                  onSave={save}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <tr key={dimension.id}>
                  <td>
                    <code>{dimension.code}</code>
                  </td>
                  <td>{dimension.name}</td>
                  <td>{dimension.sequence}</td>
                  <td className="text-end">
                    <Button size="sm" variant="link" onClick={() => startEdit(dimension)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="link" className="text-danger" onClick={() => remove(dimension)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              )
            )}

            {editing === "new" && (
              <DraftRow
                draft={draft}
                setDraft={setDraft}
                valid={valid}
                saving={saving}
                onSave={save}
                onCancel={() => setEditing(null)}
              />
            )}

            {dimensions.length === 0 && editing === null && (
              <tr>
                <td colSpan={4} className="text-secondary small py-3">
                  No dimensions. This step prices and converts as a whole.
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {editing === null && (
          <Button size="sm" variant="outline-primary" onClick={startNew}>
            Add dimension
          </Button>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

interface DraftRowProps {
  draft: Draft;
  setDraft: (draft: Draft) => void;
  valid: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

function DraftRow({ draft, setDraft, valid, saving, onSave, onCancel }: DraftRowProps) {
  return (
    <tr>
      <td>
        <Form.Control
          size="sm"
          autoFocus
          value={draft.code}
          placeholder="fall2026"
          onChange={(e) => setDraft({ ...draft, code: e.target.value })}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          value={draft.name}
          placeholder="Fall 2026"
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          value={draft.sequence}
          onChange={(e) => setDraft({ ...draft, sequence: Number(e.target.value) })}
        />
      </td>
      <td className="text-end">
        <Button size="sm" variant="primary" disabled={!valid || saving} onClick={onSave}>
          Save
        </Button>
        <Button size="sm" variant="link" onClick={onCancel}>
          Cancel
        </Button>
      </td>
    </tr>
  );
}
